import { DEFAULT_UI_LOCALE, UI_LOCALES, type UiLocale } from '@seans/contracts';
import { createInstance, type i18n as I18n } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { z } from 'zod';
import { createStoredValue, storageKey } from '../lib/storage.ts';
import en from './locales/en.json';
import pl from './locales/pl.json';
import ru from './locales/ru.json';

/** Translation bundles by interface language; `UI_LOCALES` (contracts) is the list of languages. */
const RESOURCES = {
  ru: { translation: ru },
  pl: { translation: pl },
  en: { translation: en },
} as const satisfies Record<UiLocale, { translation: object }>;

/**
 * Only a language the viewer picked in the switcher is remembered; nothing is detected or cached
 * on its own, so everyone else sees the club's default language.
 */
const chosenLocale = createStoredValue('ui-locale', z.enum(UI_LOCALES).nullable(), null);

/**
 * Earlier versions cached whatever language was resolved (then Russian) under this key, which
 * would pin those visitors to it. It never held a deliberate choice, so it is dropped.
 */
const LEGACY_LOCALE_KEY = storageKey('locale');

const forgetLegacyLocale = () => {
  try {
    window.localStorage.removeItem(LEGACY_LOCALE_KEY);
  } catch {
    // Storage unavailable: nothing was cached either.
  }
};

export const isUiLocale = (value: string): value is UiLocale =>
  (UI_LOCALES as readonly string[]).includes(value);

/** The active interface language, narrowed to the supported set. */
export const activeLocale = (i18n: I18n): UiLocale => {
  const language = i18n.resolvedLanguage ?? i18n.language;
  return isUiLocale(language) ? language : DEFAULT_UI_LOCALE;
};

/** The viewer picked a language: show it now and remember it for the next visits. */
export const chooseLocale = (i18n: I18n, locale: UiLocale): void => {
  chosenLocale.write(locale);
  void i18n.changeLanguage(locale);
};

export const createI18n = (): I18n => {
  forgetLegacyLocale();
  const i18n = createInstance();
  i18n.use(initReactI18next).init({
    resources: RESOURCES,
    lng: chosenLocale.read() ?? DEFAULT_UI_LOCALE,
    supportedLngs: [...UI_LOCALES],
    fallbackLng: DEFAULT_UI_LOCALE,
    load: 'languageOnly',
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  // <html lang> drives hyphenation, screen readers and the browser's own formatting.
  const syncDocumentLanguage = () => {
    document.documentElement.lang = activeLocale(i18n);
  };
  syncDocumentLanguage();
  i18n.on('languageChanged', syncDocumentLanguage);
  return i18n;
};
